// The outreach TEMPLATE endpoints — the CRUD page on Practice Details → Outreach
// templates reads and writes through this file and nothing else.
//
// Kept apart from ./api.js on purpose: that file is the patient-facing surface (the
// composer, the thread, the call panel). This one never touches a patient: every
// route here is a template row or a preview rendered with SAMPLE figures, so no
// PHI passes through it. Shapes are PR 2's contract; W1 owns the routes.

const API_URL = process.env.REACT_APP_API_URL || "";

// outreach_templates.kind (models_outreach.TEMPLATE_KINDS), in the order the page
// shows them. `bodyMax` mirrors the server's limit so the textarea stops where the
// server would refuse.
export const TEMPLATE_KINDS = [
  // 1,600 is Twilio's limit AFTER the 24-character "Reply STOP to opt out." line is added.
  { key: "sms", label: "SMS", bodyMax: 1576 },
  { key: "email", label: "Email", bodyMax: 20000 },
  { key: "call_script", label: "Call scripts", bodyMax: 20000 },
];

export const kindLabelOf = (kind) =>
  (TEMPLATE_KINDS.find((k) => k.key === kind) || { label: kind }).label;

export const STOP_LINE = "Reply STOP to opt out.";

// Fields every kind may use. A text or email carries a first name and a dollar
// balance and nothing else about the patient.
export const BASE_MERGE_FIELDS = [
  "first_name",
  "practice",
  "balance",
  "statement_link",
  "pay_link",
  "practice_phone",
  "payment_options",
];

// Allowed ONLY in a call script, which staff read aloud and never send: a date of
// service never leaves Atlas in a text or email. The server enforces this (400); the
// legend states it.
export const CALL_SCRIPT_ONLY_FIELDS = ["oldest_dos_month", "last_statement_date"];

export const mergeFieldsFor = (kind) =>
  kind === "call_script"
    ? [...BASE_MERGE_FIELDS, ...CALL_SCRIPT_ONLY_FIELDS]
    : BASE_MERGE_FIELDS;

// Never surface a server message we did not write. W1 returns {error: "..."} for
// the refusals a biller can act on and a generic sentence for anything else.
async function readError(response, fallback) {
  try {
    const body = await response.json();
    return { message: (body && body.error) || fallback, body: body || {} };
  } catch (err) {
    return { message: fallback, body: {} };
  }
}

// Like api.js's wrapper, plus: a 204 resolves to null (DELETE has no body).
async function request(url, options, fallback) {
  const response = await fetch(url, { credentials: "include", ...options });
  if (!response.ok) {
    const { message, body } = await readError(response, fallback);
    const error = new Error(message);
    error.status = response.status;
    error.body = body;
    throw error;
  }
  if (response.status === 204) return null;
  const body = await response.json().catch(() => null);
  if (!body || typeof body !== "object") {
    const error = new Error(
      "The response could not be read, so this may be incomplete. Try again.",
    );
    error.status = response.status;
    error.body = {};
    throw error;
  }
  return body;
}

function withBody(method, payload) {
  return {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  };
}

const templatesBase = (practiceId) =>
  `${API_URL}/api/practices/${practiceId}/outreach/templates`;

const num = (value) => {
  const n = Number(value);
  return value == null || value === "" || !Number.isFinite(n) ? null : n;
};
const str = (value) => (typeof value === "string" ? value : null);

// One template row as the CRUD page reads it. A negative id is a code constant
// (DEFAULT_TEMPLATES) standing in for a kind with no global row: it can be overridden
// but has no row to edit, disable or delete.
export function normalizeTemplateRow(raw) {
  const t = raw || {};
  const id = t.id == null ? null : Number(t.id);
  return {
    id,
    practiceId: t.practice_id == null ? null : t.practice_id,
    scope: t.scope || (t.practice_id ? "practice" : "global"),
    kind: t.kind || null,
    name: t.name || "",
    subject: str(t.subject),
    body: str(t.body) || "",
    description: str(t.description) || "",
    sortOrder: num(t.sort_order) ?? 0,
    enabled: t.enabled !== false,
    isDefault: t.is_default === true,
    overriddenBy: t.overridden_by == null ? null : Number(t.overridden_by),
    isCodeDefault: id != null && id < 0,
    updatedAt: str(t.updated_at),
  };
}

// GET …/templates?kind= → practice rows, then global rows, then code defaults, in
// the server's order.
// include_disabled=1: the CRUD page lists the practice's switched-off rows too, so
// they can be switched back on; the composer's own fetch (api.js) stays enabled-only.
export async function fetchTemplateRows(practiceId, kind) {
  const qs = new URLSearchParams({ kind, include_disabled: "1" });
  const body = await request(
    `${templatesBase(practiceId)}?${qs.toString()}`,
    {},
    "Could not load the templates.",
  );
  if (!Array.isArray(body.templates)) {
    throw new Error(
      "The response could not be read, so the templates may be incomplete. Try again.",
    );
  }
  return body.templates.map(normalizeTemplateRow);
}

// The editor's form → the wire fields. `subject` goes only with an email; the
// server ignores it elsewhere but we do not send what the kind cannot hold.
export function templatePayload(kind, form) {
  const payload = {
    name: (form.name || "").trim(),
    description: (form.description || "").trim(),
    body: form.body || "",
  };
  if (kind === "email") payload.subject = (form.subject || "").trim();
  return payload;
}

// POST …/templates → 201 {template}: a PRACTICE row (practice_id = the URL's).
// A 409 is a duplicate name for this kind; the error carries the server's sentence.
export async function createTemplate(practiceId, kind, form) {
  const body = await request(
    templatesBase(practiceId),
    withBody("POST", { kind, ...templatePayload(kind, form) }),
    "The template could not be saved.",
  );
  return normalizeTemplateRow(body.template);
}

// PUT …/templates/<id> {fields} → {template}. Partial is allowed (the Disable/Enable
// buttons send {enabled} alone); the editor sends the whole form.
export async function updateTemplate(practiceId, templateId, fields) {
  const body = await request(
    `${templatesBase(practiceId)}/${encodeURIComponent(templateId)}`,
    withBody("PUT", fields),
    "The template could not be saved.",
  );
  return normalizeTemplateRow(body.template);
}

// DELETE …/templates/<id>. 204 → {deleted: true}. A row that sent messages
// references, or a global row (super_admin), is DISABLED instead and comes back
// 200 {template} → {deleted: false, template}.
export async function deleteTemplate(practiceId, templateId) {
  const body = await request(
    `${templatesBase(practiceId)}/${encodeURIComponent(templateId)}`,
    { method: "DELETE" },
    "The template could not be deleted.",
  );
  if (body && body.template) {
    return { deleted: false, template: normalizeTemplateRow(body.template) };
  }
  return { deleted: true, template: null };
}

// POST …/templates/preview {kind, body, subject?} → the text rendered with SAMPLE
// figures (first name "Jane", $125.00, an example link). Touches no patient.
export async function previewTemplate(practiceId, { kind, body, subject }) {
  const payload = { kind, body: body || "" };
  if (kind === "email" && typeof subject === "string") payload.subject = subject;
  const answer = await request(
    `${templatesBase(practiceId)}/preview`,
    withBody("POST", payload),
    "Could not build the preview.",
  );
  return {
    body: str(answer.body) || "",
    subject: str(answer.subject),
    chars: num(answer.chars),
    segments: num(answer.segments),
  };
}

// The first line or so of a body, for the list.
export function excerpt(text, max = 120) {
  const flat = (text || "").replace(/\s+/g, " ").trim();
  if (flat.length <= max) return flat;
  return `${flat.slice(0, max - 1).trimEnd()}…`;
}
